import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { useSpring, animated } from '@react-spring/web';
import { useChatbot } from '../context/ChatbotContext';
import { useConfig } from '../context/useConfig';
import { SafeImage } from './SafeImage';
import HeroGrid from './HeroGrid';

const TEXT_COLORS = [
  'text-emerald-400',
  'text-cyan-400',
  'text-teal-400',
  'text-emerald-300',
  'text-cyan-300',
  'text-teal-300'
];

const HomePage = () => {
  const { config, isBackendAvailable } = useConfig();
  // Chatbot trigger from context
  const { openChatbot } = useChatbot();
  


  // Return early if no config (handled by App.jsx loading screen)
  if (!config) return null;

  // ================= Floating Text Elements (lightweight orbit-inspired layout) ================
  const orbitCfg = config.hero?.orbit || {};
  const maxSkills = typeof orbitCfg.maxSkills === 'number' ? orbitCfg.maxSkills : 10;
  // Accept arbitrary number of skills from config; fallback list if undefined.
  const floatingSkills = (config.hero?.floatingSkills && Array.isArray(config.hero.floatingSkills)
    ? config.hero.floatingSkills
    : ['React', 'Node.js', 'TypeScript', 'GraphQL', 'Docker', 'AWS', 'Next.js', 'Prisma'])
    .slice(0, maxSkills);

  const baseImageRadius = typeof orbitCfg.imageRadius === 'number' ? orbitCfg.imageRadius : 192;
  const defaultGap = typeof orbitCfg.gap === 'number' ? orbitCfg.gap : 120;
  const [orbitRadius, setOrbitRadius] = useState(baseImageRadius + defaultGap);
  // Half-width (px) of the hero's right column, used to keep floating labels
  // from spilling past the column edge / out of the browser window.
  const [columnHalfWidth, setColumnHalfWidth] = useState(304);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const compute = () => {
      const width = window.innerWidth;
      const scale = width < 768 ? 0.85 : width < 1024 ? 1 : width < 1440 ? 1.1 : 1.2;
      const gap = defaultGap * scale;
      return baseImageRadius + gap;
    };

    // The hero content lives in a max-w-7xl (1280px) container with responsive
    // horizontal padding; the profile/orbit lives in its right half (lg:w-1/2).
    // Derive the distance from the orbit center to the column edge so labels can
    // be clamped to stay on-screen.
    const computeColumnHalfWidth = () => {
      const width = window.innerWidth;
      const horizontalPadding = width >= 1024 ? 64 : width >= 640 ? 48 : 32; // lg:px-8 / sm:px-6 / px-4
      const containerWidth = Math.min(width, 1280) - horizontalPadding;
      // Right column is half the container on lg+, full width otherwise.
      const columnWidth = width >= 1024 ? containerWidth / 2 : containerWidth;
      return columnWidth / 2;
    };

    let frame;
    const handleResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setOrbitRadius(compute());
        setColumnHalfWidth(computeColumnHalfWidth());
      });
    };

    setOrbitRadius(compute());
    setColumnHalfWidth(computeColumnHalfWidth());
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [baseImageRadius, defaultGap]);

  // Detect reduced motion preference (guard for SSR)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mq.matches);
      const handler = (e) => setPrefersReducedMotion(e.matches);
      mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
      return () => {
        mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
      };
    }
  }, []);

  const textColors = TEXT_COLORS;

  const labelConfigs = useMemo(() => {
    if (!floatingSkills.length) return [];
    const count = floatingSkills.length;
    const step = (Math.PI * 2) / count;
    // ensure radius never collapses too close to avatar even on narrow viewports
    const effectiveRadius = Math.max(orbitRadius, baseImageRadius + defaultGap * 0.75);
    // Safety margin (px) kept between a label's outer edge and the column edge.
    const edgeMargin = 12;
    // Rough per-character width (px) for the label font (~lg/xl, semibold).
    const approxCharWidth = 9;

    return floatingSkills.map((skill, idx) => {
      const angle = step * idx;
      let x = Math.cos(angle) * effectiveRadius;
      const y = Math.sin(angle) * effectiveRadius;
      const floatOffset = 12 + (idx % 3) * 3;

      // Labels are horizontally centered on (x, y) and don't wrap, so their text
      // extends ~halfWidth to each side. Clamp x so the outer edge stays inside
      // the column, preventing long labels (e.g. "Algorithmic Trading") from
      // overflowing the browser window on smaller laptops.
      const halfWidth = (skill.length * approxCharWidth) / 2;
      const maxX = Math.max(0, columnHalfWidth - halfWidth - edgeMargin);
      x = Math.max(-maxX, Math.min(maxX, x));

      return {
        skill,
        x,
        y,
        colorClass: textColors[idx % textColors.length],
        floatOffset
      };
    });
  }, [floatingSkills, orbitRadius, textColors, baseImageRadius, defaultGap, columnHalfWidth]);

  // ================================================================================================ 
  
  // Floating elements animation
  const float1 = useSpring({
    from: { transform: 'translateY(0px)' },
    to: async (next) => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await next({ transform: 'translateY(-20px)' });
        await next({ transform: 'translateY(0px)' });
      }
    },
    config: { duration: 2000 }
  });
  
  const float2 = useSpring({
    from: { transform: 'translateY(0px)' },
    to: async (next) => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await next({ transform: 'translateY(20px)' });
        await next({ transform: 'translateY(0px)' });
      }
    },
    config: { duration: 2500 }
  });

  // Removed inline chatbot state & handlers: centralized in Chatbot component.

  return (
    <>
    {/* Changed overflow-hidden to overflow-x-hidden to prevent vertical clipping of descenders while still constraining horizontal overscroll */}
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* 3D perspective grid — clipped to the hero, behind all content (z-0) */}
      <HeroGrid prefersReducedMotion={prefersReducedMotion} />

      {/* Animated Background Elements */}
      <animated.div
        style={float1}
        className="hidden sm:block absolute top-20 left-10 w-80 h-80 bg-emerald-600/40 rounded-full mix-blend-screen blur-[100px] opacity-60"
      />
      <animated.div
        style={float2}
        className="hidden sm:block absolute top-40 right-10 w-80 h-80 bg-cyan-600/40 rounded-full mix-blend-screen blur-[100px] opacity-60"
      />
      <div className="hidden sm:block absolute -top-10 -left-10 w-80 h-80 bg-emerald-500/30 rounded-full mix-blend-screen blur-[100px] opacity-60 animate-pulse" />

      {/* Readability scrim — sits above the grid (z-0) and below content (z-10).
          Darkens the area behind the text so the grid never hurts legibility,
          while staying transparent toward the bottom so the horizon shows. */}
      <div
        className="absolute inset-0 z-[5] pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(180deg, rgba(2,11,26,0.55) 0%, rgba(2,11,26,0.35) 45%, rgba(2,11,26,0) 100%)'
        }}
      />

      {/* Main Content */}
      {/* Use the same width constraint as the rest of the site (Navbar/About/Skills)
          instead of Tailwind's stepped `container`, which snaps to breakpoint
          widths and adds large left/right margins at in-between viewport widths
          (notably in Safari), misaligning the hero heading from the navbar brand. */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-col lg:flex-row items-center justify-between min-h-[80vh]">
          
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 text-white mb-10 lg:mb-0"
          >
            {/* Greeting */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h2 className="text-2xl font-light mb-2 text-gray-300 heading-safe descender-guard">
                Hello, I&apos;m
              </h2>
              <h1 className="text-6xl lg:text-7xl font-extrabold gradient-text heading-safe descender-guard mb-4">
                {config.name}
              </h1>
            </motion.div>

            {/* TypeAnimation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-8"
            >
              {/* Removed fixed h-16 to prevent clipping of tall glyphs / wrapped lines */}
              <div className="text-3xl lg:text-4xl font-semibold text-emerald-400 heading-safe descender-guard type-animation-text [text-shadow:_0_1px_10px_rgba(2,11,26,0.9)]">
                <TypeAnimation
                  /* Build the sequence from config.hero.typingPhrases; each phrase shows for 2000ms. */
                  sequence={(config.hero?.typingPhrases || ['Developer']).flatMap(p => [p, 2000])}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                />
              </div>
            </motion.div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-xl text-gray-200 leading-relaxed mb-8 max-w-2xl paragraph-safe [text-shadow:_0_1px_8px_rgba(2,11,26,0.85)]"
            >
              {config.description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-6"
            >
              {isBackendAvailable && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openChatbot}
                  className="bg-accent-gradient hover:brightness-110 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl transition-all duration-300"
                >
                  <i className="fas fa-robot mr-2"></i>
                  Chat with AI
                </motion.button>
              )}

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#projects"
                className="relative overflow-hidden px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 text-white bg-white/5 backdrop-blur-sm border-2 border-emerald-400/70 hover:bg-accent-gradient hover:border-transparent"
              >
                View Projects
              </motion.a>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex gap-6 mt-8"
            >
              {config.socialLinks
                ?.filter(link => !link.icon.includes('twitter') && !link.icon.includes('envelope'))
                .map((link, index) => (
                  <motion.a
                    key={index}
                    whileHover={{ scale: 1.2, y: -5 }}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-2xl text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    <i className={link.icon}></i>
                  </motion.a>
                ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Profile Picture with Floating Text */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:w-1/2 flex justify-center"
          >
            <div className="relative flex items-center justify-center w-full max-w-2xl h-[500px] lg:h-[620px]">
              {/* Floating Text Elements */}
              {labelConfigs.length > 0 && (
                <div className="hidden md:block absolute inset-0 pointer-events-none z-30">
                  {labelConfigs.map(({ skill, x, y, colorClass, floatOffset }, idx) => {
                    const baseTransform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
                    const floatTransform = `translate(-50%, -50%) translate(${x}px, ${y - floatOffset}px)`;

                    return (
                      <motion.span
                        key={`${skill}-${idx}`}
                        className={`absolute top-1/2 left-1/2 font-semibold text-sm sm:text-base lg:text-lg xl:text-xl ${colorClass} whitespace-nowrap pointer-events-none select-none [text-shadow:_0_1px_8px_rgba(2,11,26,0.95),_0_0_2px_rgba(2,11,26,0.9)]`}
                        initial={{ opacity: 0, transform: baseTransform }}
                        animate={prefersReducedMotion
                          ? { opacity: 0.92, transform: baseTransform }
                          : { opacity: [0.75, 1, 0.75], transform: [baseTransform, floatTransform, baseTransform] }}
                        transition={prefersReducedMotion
                          ? { duration: 0.6 }
                          : { duration: 6 + (idx % 3), repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: idx * 0.25 }}
                        aria-hidden="true"
                      >
                        {skill}
                      </motion.span>
                    );
                  })}
                </div>
              )}

              {/* Center Profile Picture */}
              <div className="relative z-20">
                <div className="w-80 h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-8 border-white/20 shadow-2xl">
                  <SafeImage
                    src={config.profileImage || "/api/placeholder/400/400"}
                    alt={config.name}
                    className="w-full h-full object-cover"
                    draggable="false"
                    fetchpriority="high" // eslint-disable-line react/no-unknown-property
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>

              {/* Glow Effect */}
              <div className="absolute inset-0 z-10 bg-accent-gradient rounded-full blur-3xl opacity-25 animate-pulse" aria-hidden />
            </div>
          </motion.div>
        </div>
      </div>
      {/* Chatbot modal removed; single global Chatbot component handles display. */}
    </div>
    </>
  );
};

export default HomePage;